import { PostHog, usePostHog } from 'posthog-js/react';
import { useEffect, useState, useMemo } from 'react';
import { useLogger } from './logging/logger-context';
import { ILogger } from './logging/interface';

export interface ZendeskBrandConfig {
  defaultLanguage: string;
  chat: string;
}

export interface ZendeskConfig {
  brands: Record<string, ZendeskBrandConfig>;
}

const ZENDESK_CONFIG_FEATURE_FLAG = 'zendesk-ai-chat';

// Cache for the config to avoid reloading on every hook call
let configCache: ZendeskConfig | null = null;

/**
 * Hook to get Zendesk chat key based on the charger's whiteLabellingId
 */
export const useZendeskChatConfig = (whiteLabellingId: string | null): string | null => {
  const { logger } = useLogger();
  const posthog = usePostHog();
  const [config, setConfig] = useState<ZendeskConfig | null>(null);

  useEffect(() => {
    if (!posthog) {
      return null;
    }
    setConfig(loadZendeskConfig(posthog, logger));
  }, [posthog]);

  return useMemo(() => {
    return getZendeskChatKey(config, whiteLabellingId);
  }, [config, whiteLabellingId]);
};

/**
 * Loads Zendesk configuration from PostHog feature flag payload.
 * Returns null if config cannot be loaded or parsed.
 * Uses caching to avoid multiple simultaneous loads.
 */
const loadZendeskConfig = (posthog: PostHog, logger: ILogger): ZendeskConfig | null => {
  if (configCache) {
    return configCache;
  }

  try {
    const featureFlagPayload = posthog.getFeatureFlagPayload(ZENDESK_CONFIG_FEATURE_FLAG);
    if (!featureFlagPayload) {
      throw new Error('Zendesk config feature flag payload is not available');
    }

    const config = parseZendeskConfig(featureFlagPayload as unknown as ZendeskConfig);
    if (config) {
      configCache = config;
      return config;
    }
  } catch (e: unknown) {
    logger.error('Failed to load Zendesk config from feature flag:', e as Error);
  }

  return null;
};

/**
 * Parses JSON data into a ZendeskConfig model
 */
const parseZendeskConfig = (featureFlagPayload: ZendeskConfig): ZendeskConfig => {
  const brandsMap = featureFlagPayload.brands;
  const brands: Record<string, ZendeskBrandConfig> = {};

  if (brandsMap) {
    for (const [whiteLabellingId, brandData] of Object.entries(brandsMap)) {
      if (brandData?.chat) {
        brands[whiteLabellingId] = {
          defaultLanguage: brandData.defaultLanguage || 'en',
          chat: brandData.chat,
        };
      }
    }
  }

  return {
    brands,
  };
};

/**
 * Retrieves the Zendesk chat key for a specific whiteLabellingId.
 */
const getZendeskChatKey = (
  config: ZendeskConfig | null,
  whiteLabellingId: string | null | undefined,
): string | null => {
  if (!whiteLabellingId || whiteLabellingId.trim() === '' || !config) return null;

  return config.brands[whiteLabellingId]?.chat ?? null;
};
